import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable } from 'rxjs';
import firebase from 'firebase/compat';
import User = firebase.User;
import { App } from '@capacitor/app';

@Injectable({
  providedIn: 'root',
})
export class BaseAuthenticationService {
  userData: Observable<User>;
  user: any = null;
  protected _idToken: string = null;
  loggedInUser = false;

  protected onLogin(user: User): void {}
  protected onLogout(): void {}
  protected onUserStateChange(user: User | null): void {}

  constructor(protected angularFireAuth: AngularFireAuth) {
    this.userData = angularFireAuth.authState;
    this.angularFireAuth.authState.subscribe((user) => {
      if (user) {
        this.user = user;
        this.user.getIdToken(false).then((token: string) => {
          this._idToken = token;
        });
        this.loggedInUser = true;
        this.onLogin(user);
      } else {
        this.user = null;
        this.loggedInUser = false;
        this._idToken = null;
        this.onLogout();
      }
      this.onUserStateChange(user);
    });
    this.setupAppStateListener();
  }

  protected setupAppStateListener() {
    App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        const currentUser = await this.angularFireAuth.currentUser;
        if (currentUser) {
          try {
            const token = await currentUser.getIdToken(false);
            this._idToken = token;
            this.user = currentUser;
            this.loggedInUser = true;
          } catch (error) {
            this.handleSessionExpiry();
          }
        } else {
          // Only handle session expiry if user was previously logged in (session expired).
          // When user was never logged in (e.g. on register/login page), do not redirect.
          if (this.loggedInUser) {
            this.handleSessionExpiry();
          }
        }
      }
    });
  }

  protected handleSessionExpiry() {
    this.user = null;
    this.loggedInUser = false;
    this._idToken = null;
    this.SignOut();
  }

  isLoggedIn(): Observable<User | null> {
    return this.angularFireAuth.authState;
  }

  public async getIdToken(): Promise<string> {
    if (!this.user) {
      throw new Error('No authenticated user');
    }
    try {
      const token = await this.user.getIdToken(false);
      this._idToken = token;
      return token;
    } catch (error) {
      console.error('[BaseAuthService] Error getting fresh ID token:', error);
      throw error;
    }
  }

  public get idToken(): string {
    if (!this._idToken) {
      console.warn('[BaseAuthService] idToken getter called but no token available. Use getIdToken() instead.');
    }
    return this._idToken;
  }

  protected setIdToken(token: string) {
    this._idToken = token;
  }

  SignUp(email: string, password: string): Promise<firebase.auth.UserCredential> {
    return this.angularFireAuth.createUserWithEmailAndPassword(email, password);
  }

  SignIn(email: string, password: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.angularFireAuth
        .signInWithEmailAndPassword(email, password)
        .then((res) => {
          if (res && res.user && !res.user.emailVerified) {
            console.log('Email is not verified');
            res.user.sendEmailVerification();
          }
          resolve(true);
        })
        .catch((err) => {
          resolve(false);
        });
    });
  }

  SignOut() {
    this.angularFireAuth.signOut();
  }

  async ForgotPassword(email: string): Promise<void> {
    console.log('[BaseAuthService] ForgotPassword initiated for email:', email);
    try {
      await this.angularFireAuth.sendPasswordResetEmail(email, {
        handleCodeInApp: false,
        url: window.location.origin, // Ensures the reset link points back to your app
      });
      console.log('[BaseAuthService] Password reset email sent successfully to:', email);
      console.log('[BaseAuthService] User should check spam folder if email not received');
      return;
    } catch (error) {
      console.error('[BaseAuthService] Error sending password reset email to:', email, 'Error:', error);

      // Log specific Firebase auth errors for better debugging
      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string; message?: string };
        console.error('[BaseAuthService] Firebase error code:', firebaseError.code);
        switch (firebaseError.code) {
          case 'auth/user-not-found':
            console.error('[BaseAuthService] No user found with this email address');
            break;
          case 'auth/invalid-email':
            console.error('[BaseAuthService] Invalid email format provided');
            break;
          case 'auth/too-many-requests':
            console.error('[BaseAuthService] Too many requests, please try again later');
            break;
          default:
            console.error('[BaseAuthService] Unknown Firebase auth error');
        }
      }

      throw error;
    }
  }

  async refreshToken() {
    const user = await this.angularFireAuth.currentUser;
    const token = await user.getIdToken(true);
  }
}
